import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveUrl, isUploadedVideo, getEmbedUrl, imageUrls } from "../../lib/media";
import { frameStyle, frameAspectClass, MAXW_CLASS } from "../../lib/frame";
import { FramedImage } from "./FramedImage";

// Classes du conteneur d'un média (pages détail), pilotées par les options du frame :
// format (horizontal/vertical/carré/original), fond, bordure, largeur max.
export const mediaWrapClass = (frame, fallback = "aspect-video") => {
  const f = frame || {};
  const asp = frameAspectClass(f, fallback); // null = proportions d'origine (bloc auto-ajusté)
  const wrap = f.format === "vertical" ? "max-w-sm mx-auto" : f.format === "square" ? "max-w-xl mx-auto" : (MAXW_CLASS[f.maxW] || "");
  return {
    intrinsic: asp === null,
    aspect: asp,
    wrap,
    cls: `w-full ${asp || ""} ${wrap} rounded-xl overflow-hidden ${f.border ? "border border-white/25" : "border border-white/10"} ${f.noBg ? "" : "bg-[#0e0e0e]"}`,
  };
};

// Detect constrained connections to prefer lightweight media (images) over video
const preferLightMedia = () => {
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    return !!c.saveData || ["slow-2g", "2g", "3g"].includes(c.effectiveType);
  } catch { return false; }
};

const Slideshow = ({ images = [], alt, className, fit = "cover" }) => {
  const [i, setI] = useState(0);
  const pics = imageUrls(images);
  useEffect(() => {
    if (pics.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % pics.length), 4000);
    return () => clearInterval(t);
  }, [pics.length]);
  if (!pics.length) return null;
  const fitCls = fit === "contain" ? "object-contain" : "object-cover";
  return (
    <div className={`relative overflow-hidden ${className || ""}`} data-testid="media-slideshow">
      {pics.map((p, idx) => (
        <img key={idx} src={resolveUrl(p)} alt={alt} loading="lazy"
          className={`absolute inset-0 w-full h-full ${fitCls} transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0"}`} />
      ))}
      {/* keep layout height */}
      <img src={resolveUrl(pics[0])} alt="" className={`w-full h-full ${fitCls} invisible`} />
      {pics.length > 1 && <>
        <button onClick={(e) => { e.preventDefault(); setI((x) => (x - 1 + pics.length) % pics.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-black/50 border border-white/20 text-white flex items-center justify-center hover:border-electric"><ChevronLeft size={16} /></button>
        <button onClick={(e) => { e.preventDefault(); setI((x) => (x + 1) % pics.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-black/50 border border-white/20 text-white flex items-center justify-center hover:border-electric"><ChevronRight size={16} /></button>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
          {pics.map((_, idx) => <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-electric" : "w-1.5 bg-white/40"}`} />)}
        </div>
      </>}
    </div>
  );
};

export const MediaView = ({ item, className, alt = "", fit = "cover", frame }) => {
  const type = item?.mediaType || "image";
  const light = preferLightMedia();
  const imgFit = fit === "contain" ? "object-contain" : "object-cover";
  const fStyle = frame ? frameStyle(frame, fit) : undefined;
  // On slow connections, prefer a still image / slideshow instead of video for smoothness
  if (type === "video" && light && (item.image || (item.gallery || []).length)) {
    if ((item.gallery || []).length) return <Slideshow images={item.gallery} alt={alt} fit={fit} className={`w-full h-full ${className || ""}`} />;
    return <img src={resolveUrl(item.image)} alt={alt} loading="lazy" style={fStyle} className={`w-full h-full ${imgFit} ${className || ""}`} data-testid="media-image" />;
  }
  if (type === "video" && item.videoUrl) {
    if (isUploadedVideo(item.videoUrl)) {
      return <video src={resolveUrl(item.videoUrl)} poster={resolveUrl(item.image)} controls playsInline preload="metadata"
        className={`w-full h-full object-contain ${frame?.noBg ? "" : "bg-black"} ${className || ""}`} data-testid="media-video" />;
    }
    return <iframe src={getEmbedUrl(item.videoUrl)} title={alt} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className={`w-full h-full ${className || ""}`} data-testid="media-embed" />;
  }
  if (type === "slideshow" && (item.gallery || []).length) {
    return <Slideshow images={item.gallery} alt={alt} fit={fit} className={`w-full h-full ${className || ""}`} />;
  }
  if (!item?.image) return <div className={`w-full h-full bg-[#0e0e0e] ${className || ""}`} />;
  // Format « proportions d'origine » : le bloc épouse l'image (jamais recadrée ni déformée).
  if (frame?.format === "original") return <FramedImage src={item.image} frame={frame} effects={undefined} alt={alt} testid="media-image" />;
  return <img src={resolveUrl(item.image)} alt={alt} loading="lazy" style={fStyle} className={`w-full h-full ${imgFit} ${className || ""}`} data-testid="media-image" />;
};
